/**
 * A treatment's shot list.
 *
 * Shots are one flat, ordered list. A shot with a `parent_id` is a setup
 * inside that parent's sequence; a top-level shot and its setups form a
 * "block". Numbers (01, 04A) come from the order and are never stored.
 *
 * The backend twin is backend/treatment_service.py. Both are held to the
 * cases in treatmentShots.cases.json - change one side, change the other.
 *
 * Every function here is pure and returns a new list, so the editor can use
 * them inside `setTreatment(prev => ...)`.
 */

/** 0 -> A, 25 -> Z, 26 -> AA, like spreadsheet columns. */
function letters(index) {
  let out = '';
  let n = index + 1;
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

/** Map each shot id to its number: "01" for a shot, "04A" for a setup. */
export function shotNumbers(shots) {
  const numbers = {};
  const setups = {};
  let top = 0;
  for (const shot of shots || []) {
    if (shot.parent_id) {
      const n = setups[shot.parent_id] || 0;
      setups[shot.parent_id] = n + 1;
      numbers[shot.id] = `${numbers[shot.parent_id] || '??'}${letters(n)}`;
    } else {
      top += 1;
      numbers[shot.id] = String(top).padStart(2, '0');
    }
  }
  return numbers;
}

/** Why this list cannot be saved, or null. Mirrors shot_list_error. */
export function shotListError(shots) {
  const seen = new Set();
  const topLevel = new Set();
  let currentBlock = null;
  for (const shot of shots || []) {
    if (seen.has(shot.id)) return `Shot ${shot.id} appears twice`;
    seen.add(shot.id);
    if (!shot.parent_id) {
      topLevel.add(shot.id);
      currentBlock = shot.id;
    } else if (!topLevel.has(shot.parent_id)) {
      return "A setup's sequence must be a top-level shot listed before it";
    } else if (shot.parent_id !== currentBlock) {
      return 'A setup must sit with the rest of its sequence';
    }
  }
  return null;
}

/** The list split into blocks: [[shot, ...its setups], ...]. */
export function shotBlocks(shots) {
  const blocks = [];
  for (const shot of shots || []) {
    if (shot.parent_id && blocks.length) blocks[blocks.length - 1].push(shot);
    else blocks.push([shot]);
  }
  return blocks;
}

export const hasSetups = (shots, id) => (shots || []).some((s) => s.parent_id === id);

export function newShot(parentId = null) {
  return {
    id: crypto.randomUUID(),
    parent_id: parentId,
    title: '',
    framing: '',
    movement: '',
    duration_seconds: null,
    location: '',
    description: '',
    references: [],
    equipment: [],
  };
}

/**
 * Add `shot`: a setup as the last setup of its sequence; a top-level shot at
 * the end, or - given `afterId`, the shot the user is on - straight after
 * that shot's whole block, since a shot dropped between a sequence's setups
 * would read as one of them. An `afterId` that isn't in the list appends.
 */
export function insertShot(shots, shot, afterId = null) {
  if (!shot.parent_id) {
    const after = shots.find((s) => s.id === afterId);
    if (!after) return [...shots, shot];
    const blockId = after.parent_id || after.id;
    const lastInBlock = shots.reduce((at, s, i) => (s.id === blockId || s.parent_id === blockId ? i : at), -1);
    return [...shots.slice(0, lastInBlock + 1), shot, ...shots.slice(lastInBlock + 1)];
  }
  const lastInBlock = shots.reduce(
    (at, s, i) => (s.id === shot.parent_id || s.parent_id === shot.parent_id ? i : at),
    -1,
  );
  if (lastInBlock === -1) return [...shots, { ...shot, parent_id: null }];
  return [...shots.slice(0, lastInBlock + 1), shot, ...shots.slice(lastInBlock + 1)];
}

/** Remove a shot. Removing a sequence removes its setups with it. */
export function removeShot(shots, id) {
  return shots.filter((s) => s.id !== id && s.parent_id !== id);
}

/**
 * Move `activeId` to where `overId` is, as a drag-and-drop would.
 *
 * A top-level shot moves with its whole block. Dropped onto a setup, it is
 * dropped onto that setup's sequence - on the way past a sequence a drag
 * hovers over its setups first.
 *
 * A setup moves among setups, in its own sequence or another one: dropped on
 * any setup it joins that setup's sequence at that spot, and dropped on
 * another sequence's own row it becomes that sequence's first setup. A setup
 * dropped on a plain shot stays where it was - it never leaves sequences
 * altogether by accident. A sequence whose last setup leaves becomes a plain
 * shot again.
 */
export function moveShot(shots, activeId, overId) {
  if (activeId === overId) return shots;
  const active = shots.find((s) => s.id === activeId);
  const over = shots.find((s) => s.id === overId);
  if (!active || !over) return shots;

  if (active.parent_id) {
    const rest = shots.filter((s) => s !== active);
    if (over.parent_id) {
      // Splicing at over's old index puts the setup after over when it moved
      // down the list and before it when it moved up, as the drag showed.
      rest.splice(shots.indexOf(over), 0, { ...active, parent_id: over.parent_id });
      return rest;
    }
    if (over.id === active.parent_id || !hasSetups(shots, over.id)) return shots;
    rest.splice(rest.indexOf(over) + 1, 0, { ...active, parent_id: over.id });
    return rest;
  }

  const target = over.parent_id || over.id;
  if (target === activeId) return shots;
  const blocks = shotBlocks(shots);
  const from = blocks.findIndex((b) => b[0].id === activeId);
  const to = blocks.findIndex((b) => b[0].id === target);
  const [moved] = blocks.splice(from, 1);
  blocks.splice(to, 0, moved);
  return blocks.flat();
}

/**
 * Running time in seconds. A sequence with setups counts its setups, not
 * itself, so a sequence is never counted twice. Blank durations count as 0.
 */
export function totalDurationSeconds(shots) {
  return (shots || [])
    .filter((s) => !hasSetups(shots, s.id))
    .reduce((sum, s) => sum + (Number(s.duration_seconds) || 0), 0);
}

/**
 * Set who supplies a hire on every shot row behind it. The row ids come from
 * the sourcing roll-up, so the backend's merge rule is not repeated here.
 */
export function assignSupplier(shots, equipmentIds, supplierId) {
  const ids = new Set(equipmentIds);
  return (shots || []).map((s) => ({
    ...s,
    equipment: (s.equipment || []).map((e) => (ids.has(e.id) ? { ...e, supplier_id: supplierId } : e)),
  }));
}

/** A copy of one shot with fresh ids for it, its frames and its equipment. */
function copyShot(shot, parentId, id = crypto.randomUUID()) {
  return {
    ...shot,
    id,
    parent_id: parentId,
    references: (shot.references || []).map((r) => ({ ...r, id: crypto.randomUUID() })),
    equipment: (shot.equipment || []).map((e) => ({ ...e, id: crypto.randomUUID() })),
  };
}

/**
 * Duplicate a shot, placing the copy straight after the original.
 *
 * A sequence is copied whole - its setups too - after its last setup. A setup
 * is copied within its own sequence. Returns { shots, copyId }, copyId being
 * the new shot (or the new sequence) so the editor can select it. `copyId`
 * may be given, so a caller updating state functionally knows it up front.
 */
export function duplicateShot(shots, id, copyId = crypto.randomUUID()) {
  const index = shots.findIndex((s) => s.id === id);
  if (index === -1) return { shots, copyId: null };
  const shot = shots[index];

  if (shot.parent_id) {
    const copy = copyShot(shot, shot.parent_id, copyId);
    return { shots: [...shots.slice(0, index + 1), copy, ...shots.slice(index + 1)], copyId: copy.id };
  }

  const block = shotBlocks(shots).find((b) => b[0].id === id);
  const parentCopy = copyShot(block[0], null, copyId);
  const copies = [parentCopy, ...block.slice(1).map((s) => copyShot(s, parentCopy.id))];
  const after = index + block.length;
  return { shots: [...shots.slice(0, after), ...copies, ...shots.slice(after)], copyId: parentCopy.id };
}
